import { PlatformLocation } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';

import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { ErrorId } from '../error-handler/error-id';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpDataResponse, InstrumentedHttpPromise } from '../shared/instrumentation/instrumented-http.service';
import { AttachmentType, EmailReference } from '../shared/models/email-attachment.model';
import { FileItem, FileType, SourceProvenance } from '../shared/models/file-item.model';
import { HttpRequestOptions } from '../shared/models/http-request-options.model';
import { OperationStream } from '../shared/models/operation-stream.model';
import { MruConfigService } from '../shared/mru/mru-config.service';
import { MruDataItem } from '../shared/mru/mru-data-item.model';
import { MruData } from '../shared/mru/mru-data.service';
import { MruPlusPlusV2Service } from '../shared/mru/mru-plus-plus-v2.service';
import { AadAppId, AAD_APP_ID } from '../shared/services/aad-app-id.injection-token';
import { AccessTokenConsumer, AccessTokenService } from '../shared/services/access-token.service';
import { OfficeHomeApiConfigService } from '../shared/services/officehome-api-config.service';
import { SchedulerService } from '../shared/services/scheduler.service';
import { UpdateContentService } from '../shared/services/update-content.service';
import { WindowService } from '../shared/services/window.service';
import { XhrService } from '../shared/services/xhr.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { GuidGenerator } from '../shared/utilities/guid-generator.utility';
import { ObservableHelper } from '../shared/utilities/observable-helper.utility';

interface RawRecommendedResponseV1 {
    diagnostics_data: any;
    documents_group: RecommendedDocumentsV1;
    error_data: any;
    response_code: number;
    recommendedRequestId?: string;
}

interface RecommendedDocumentsV1 {
    documents: Array<RawRecommendedDocumentV1>;
    ranker: string;
    title: string;
}

interface RawRecommendedDocumentV1 {
    activity_info: RawRecommendedActivityInfo;
    app: string;
    app_info: RawRecommendedAppInfo;
    creation_info: RawRecommendedCreationInfo;
    extension: string;
    file_size: number;
    id: string;
    modification_info: RawRecommendedCreationInfo;
    onedrive_info: OneDriveInfo;
    resource_id: string;
    score: number;
    server_doc_id: string;
    service_info: ServiceInfo;
    sharepoint_info: SharepointInfo;
    title: string;
    url: string;
    web_url?: string;
}

interface RawRecommendedActivityInfo {
    badge: RawRecommendedActivityBadge;
    is_collab: boolean;
}

interface RawRecommendedActivityBadge {
    message_format: string;
    navigation_info: RawRecommendedNavigationInfo;
    timestamp: string;
    type: number;
    users: Array<RawRecommendedUser>;
    conversation_info: RawRecommendedConversationInfo;
    sharing_action: RawRecommendedSharingAction;
}

interface RawRecommendedSharingAction {
    medium: RawRecommendedSharingMedium;
    web_url: string;
}

const enum RawRecommendedSharingMedium {
    Default = 0,
    Teams = 1,
    Calendar = 2
}

interface RawRecommendedConversationInfo {
    subject: string;
}

interface RawRecommendedNavigationInfo {
    id: string;
}

interface RawRecommendedUser {
    display_name: string;
    upn: string;
}

interface RawRecommendedCreationInfo {
    timestamp: string;
    user_info: any; //check what this is
}

interface RawRecommendedAppInfo {
    icon_url: string;
}

interface ServiceInfo {
    service_id: string;
}

export interface SharepointInfo {
    list_id: string;
    list_item_unique_id: string;
    site_id: string;
    site_url: string;
    web_id: string;
}

export interface OneDriveInfo {
    drive_id: string;
    item_id: string;
}

interface RecommendedDismissRequestBody {
    id: string;
    until?: Date;
}

@Injectable()
export class RecommendedServiceV1 extends XhrService {
    public static readonly clientRequestIdHeader = 'client-request-id';

    private readonly recommendedDataStreams: Map<string, OperationStream<MruData>>;
    private readonly allApps: Array<string>;
    private readonly baseUrl: string;
    private readonly sources: Array<string>;

    private static readonly countParam = 'show=';
    private static readonly skipCacheParam = 'skipCache=false';
    private static readonly appsParam = 'apps=';
    private static readonly locParam = 'rs=';
    private static readonly sourcesParam = 'sources=';

    private fetchRequestIndex = 0;

    constructor(
        accessTokenService: AccessTokenService,
        private readonly capabilitiesService: CapabilitiesService,
        contextService: ContextService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        private readonly mruConfigService: MruConfigService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        platformLocation: PlatformLocation,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly updateContentService: UpdateContentService,
        scheduler: SchedulerService,
        private readonly windowService: WindowService,
        @Inject(AAD_APP_ID) private readonly aadAppId: AadAppId
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, AccessTokenConsumer.Substrate, contextService, officeHomeApiConfigService, platformLocation, scheduler);
        const mruConfig = this.mruConfigService.config;

        // Recommended Section for Stream requires app value as Video
        this.allApps = ['Word', 'Excel', 'PowerPoint', 'OneNote', 'Video', 'PlayList' ];
        if (mruConfig.recommendedPdfEnabled) {
            this.allApps.push('Pdf');
        }
        if (mruConfig.recommendedVisioEnabled) {
            this.allApps.push('Visio');
        }
        if (mruConfig.recommendedFluidEnabled) {
            this.allApps.push('Fluid', 'Bohemia', 'Loop');
        }

        this.sources = ['EmailAttachment', 'OneDrive', 'Sharepoint'];
        if (this.capabilitiesService.capabilities.isPwaInUwp) {
            this.sources.push('LocalFile');
        }

        this.baseUrl = (this.inMockMode ? this.contextService.sharedContext.officeHomeApiRoot : mruConfig.substrateApiEndpoint) +
            `/${mruConfig.recommendedB2ApiRootPath}/`;

        this.recommendedDataStreams = new Map<string, OperationStream<MruData>>();
    }

    public getRecommendedDocs(types?: Set<FileType>): OperationStream<MruData> {
        const url = this.getUrl(types);

        if (this.recommendedDataStreams.has(url)) {
            return this.recommendedDataStreams.get(url);
        }

        const recommendedFromNetwork = this.capabilitiesService.capabilities.prefetchDataEnabled ?
            this.prefetchRecommendedDocs(url) : this.fetchRecommendedDocs(FetchStrategy.NetworkFirst, url, types);
        const updateStream = this.updateContentService.getOrAddUpdateStream(this.getUpdateStreamKey(url), () => this.fetchRecommendedDocs(FetchStrategy.NetworkFirst, url, types));

        const recommendedDataStream = ObservableHelper.createResultsWithCacheObservable(
            this.fetchRecommendedDocs(FetchStrategy.CacheOnly, url, types),
            recommendedFromNetwork,
            updateStream,
            _ => this.recommendedDataStreams.delete(url) // clear the cached stream on failure so retry is possible
        );

        this.recommendedDataStreams.set(url, recommendedDataStream);

        return recommendedDataStream;
    }

    public updateRecommendedDocs(types?: Set<FileType>): Promise<void> {
        return this.updateContentService.pushUpdate(this.getUpdateStreamKey(this.getUrl(types)));
    }

    public removeDocument(docId: string, snoozeUntil?: Date): Promise<void> {
        const requestId = GuidGenerator.createGuid();
        const requestConfig = this.createRequestConfig(requestId);

        return new Promise<void>((resolve, reject) => {
            const url = this.baseUrl + this.mruConfigService.config.recommendedApiDismissedDocumentsPath;
            const deleteBody: RecommendedDismissRequestBody = {
                id: docId,
                until: snoozeUntil
            };

            const promiseCallback = (promise: InstrumentedHttpPromise<boolean>) => {
                promise.then(() => resolve(null));
            };

            this.post(KnownInstrumentationEvents.RecommendedDelete, KnownRequestInstrumentationEvents.RecommendedDelete, url, deleteBody, promiseCallback, reject, requestConfig);
        });
    }

    public fetchRecommendedDocs(strategy: FetchStrategy, url: string, types?: Set<FileType>): Promise<MruData> {
        return new Promise<MruData>((resolve, reject) => {
            const requestId = GuidGenerator.createGuid();
            const requestConfig = this.createRequestConfig(requestId);
            const promiseCallback = (promise: InstrumentedHttpPromise<RawRecommendedResponseV1>) => {
                promise
                    .instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                        params[InstrumentationParam.DebugInfo] = this.getServerDebugInfo(result);
                        params[InstrumentationParam.Index] = (this.fetchRequestIndex++).toString();
                        params[InstrumentationParam.ImpressionId] = (!!result.headers && result.headers.get(RecommendedServiceV1.clientRequestIdHeader)) || requestId;

                        // TODO: Remove once cause of repeat calls is understood (ETA 10/25/19)
                        params[InstrumentationParam.DocumentType] = types ? this.getTypesParam(types) : 'All';

                        if (result.ok && result.data) {
                            let itemsCount = 0;
                            if (result.data.documents_group && result.data.documents_group.documents) {
                                itemsCount = result.data.documents_group.documents.length;
                                params[InstrumentationParam.ResourceIdDistribution] = result.data.documents_group.documents.map(d => d.resource_id).toString();
                            }
                            params[InstrumentationParam.ActualSize] = itemsCount.toString();
                        }
                    })
                    .then(data => this.processLocalRecommendedData(data.data)
                        .then(processedLocalData => {
                            resolve({
                                items: processedLocalData || [],
                                impressionId: !!data.headers && data.headers.get(RecommendedServiceV1.clientRequestIdHeader) || requestId,
                                fromCache: strategy === FetchStrategy.CacheOnly
                            });
                        }))
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.QG6EV);
                    });
            };

            this.get<RawRecommendedResponseV1>(
                KnownInstrumentationEvents.RecommendedRequest,
                KnownRequestInstrumentationEvents.RecommendedRequest,
                url,
                promiseCallback,
                reject,
                requestConfig,
                strategy
            );
        });
    }

    public prefetchRecommendedDocs(url: string): Promise<MruData> {
        if (this.windowService.nativeWindow.prefetchRecommendedDataPromise) {
            return this.windowService.nativeWindow.prefetchRecommendedDataPromise.then((data: RawRecommendedResponseV1) => {
                if (!data) {
                    return Promise.reject('Prefetch failed');
                }
                return this.processLocalRecommendedData(data)
                    .then(processedLocalData => ({
                        items: processedLocalData,
                        impressionId: data.recommendedRequestId || '',
                        fromCache: false
                    }));
            });
        } else {
            return this.fetchRecommendedDocs(FetchStrategy.NetworkFirst, url);
        }
    }

    protected handleQueryParam(paramNameInLowerCase: string, value: string | Array<string>, requestConfig: HttpRequestOptions) {
        if (paramNameInLowerCase === 'mockrecommended' && this.mockRequestHeader) {
            requestConfig.headers.set(this.mockRequestHeader, value);
        }
    }

    private static fileType(item: RawRecommendedDocumentV1): FileType {
        let fileType = FileItem.fileTypeFromExtension(item.extension);

        if (!fileType || fileType === FileType.None) {
            fileType = RecommendedServiceV1.fileTypeFromAppName(item.app) || FileType.Unknown;
        }
        return fileType;
    }

    private static fileTypeFromAppName(application: string): FileType {
        if (!application) {
            return null;
        }
        return FileItem.fileTypeNameToFileType(application);
    }

    private createRequestConfig(requestId: string): HttpRequestOptions {
        const startupConfig = this.startupConfigService.config;
        const headers = new Map<string, string | Array<string>>([
            ['X-AnchorMailbox', `${startupConfig.objectId}@${startupConfig.tenantId}`],
            ['X-Office-Application', this.contextService.sharedContext.recommendedIceApplicationId.toString()],
            ['X-Office-Scenario', this.contextService.getCurrentPageForLogging()],
            ['X-RoutingParameter-SessionKey', startupConfig.objectId],
            ['X-Ms-ClientAppId', this.aadAppId],
            [RecommendedServiceV1.clientRequestIdHeader, requestId]
        ]);

        if (!!this.capabilitiesService.capabilities.threeSRdlFlightHeader) {
            headers.set('X-Client-Flights', this.capabilitiesService.capabilities.threeSRdlFlightHeader);
        }

        return {
            headers
        };
    }

    private getUrl(types?: Set<FileType>): string {
        const appsList = this.getTypesParam(types);
        const sourcesList = this.sources.join(',');
        return `${this.baseUrl}${this.mruConfigService.config.recommendedB2ApiFilesPath}?${RecommendedServiceV1.countParam}${this.mruConfigService.config.numRecommendedToRequest}&${RecommendedServiceV1.skipCacheParam}&${RecommendedServiceV1.appsParam}${appsList}&${RecommendedServiceV1.locParam}${this.contextService.sharedContext.officeMarket}&${RecommendedServiceV1.sourcesParam}${sourcesList}`;
    }

    private getTypesParam(types?: Set<FileType>): string {
        const appsList = types && types.size ? this.allApps.filter(type => types.has(FileItem.fileTypeNameToFileType(type))) : this.allApps;
        return appsList.join(',');
    }

    private getUpdateStreamKey(url: string): string {
        return `recommended-${url}`;
    }

    private convertToMruDataItem(recommendedItem: RawRecommendedDocumentV1, isLocal: boolean = false): MruDataItem {
        const activityBadge = recommendedItem.activity_info ? recommendedItem.activity_info.badge : undefined;
        const itemTimestamp = activityBadge && activityBadge.timestamp ? new Date(activityBadge.timestamp) : null;

        const isPlaylist = recommendedItem.app === 'PlayList';
        const isEmailAttachment = recommendedItem.service_info.service_id === 'EmailAttachment' || recommendedItem.service_info.service_id === 'FP_EXCHANGE_ORGID';
        //TODO: Remove 'EmailAttachment' check as Recommended is updating their values
        let emailReference: EmailReference = null;
        if (isEmailAttachment) {
            emailReference = {
                AttachmentType: AttachmentType.Classic, // If source is 'EmailAttachment' it is always a Classic attachment. Modern attachments have source = "OneDrive" or "SharePoint".
                ConversationId: '',
                Subject: activityBadge && activityBadge.conversation_info ? activityBadge.conversation_info.subject : ''
            };
        }

        let teamsDeepLink = undefined;
        if (this.capabilitiesService.capabilities.recommendedTeamsLinkEnabled && activityBadge && activityBadge.sharing_action && activityBadge.sharing_action.medium === RawRecommendedSharingMedium.Teams) {
            teamsDeepLink = activityBadge.sharing_action.web_url;
        }

        return {
            Application: recommendedItem.app,
            Category: 'Document', //TODO
            DocId: recommendedItem.onedrive_info && recommendedItem.onedrive_info.item_id, // EmailAttachments will not have onedrive_info or sharepoint_info
            DriveId: recommendedItem.onedrive_info && recommendedItem.onedrive_info.drive_id,
            Extension: recommendedItem.extension || FileItem.getFileExtensionFromUrl(recommendedItem.url),
            FileName: recommendedItem.title,
            Title: recommendedItem.title,
            Url: isEmailAttachment || isPlaylist || isLocal ? recommendedItem.web_url : recommendedItem.url,
            WebUrl: recommendedItem.web_url,
            LastAccessedTime: itemTimestamp,
            FileType: FileType[RecommendedServiceV1.fileType(recommendedItem)],
            PathArray: null,
            Provenance: SourceProvenance[SourceProvenance.Substrate],
            ResourceId: recommendedItem.resource_id,
            UrlAlreadyEncoded: true,
            Activity: recommendedItem.activity_info ? MruPlusPlusV2Service.convertMruActivityToActivityDescription(recommendedItem.activity_info) : undefined,
            IsCollab: recommendedItem.activity_info ? recommendedItem.activity_info.is_collab : false,
            IsEmailAttachment: isEmailAttachment,
            IsLocal: isLocal,
            OneDriveInfo: recommendedItem.onedrive_info,
            SharepointInfo: recommendedItem.sharepoint_info,
            SPOId: recommendedItem.id,
            EmailReference: emailReference,
            NavigationId: recommendedItem.activity_info && recommendedItem.activity_info.badge && recommendedItem.activity_info.badge.navigation_info ? recommendedItem.activity_info.badge.navigation_info.id : null,
            TeamsDeepLink: teamsDeepLink,
            IconUrl: recommendedItem.app_info?.icon_url
        };
    }

    private async processLocalRecommendedData(data: RawRecommendedResponseV1)
        : Promise<Array<MruDataItem>> {
        const rawItems = data?.documents_group?.documents || [];
        return Promise.all(rawItems.map(element => this.checkLocalFileAndProcess(element)))
            .then(result => {
                result = result.filter(item => item !== null && item.Activity && item.Activity.formatString);
                return result;
            });
    }

    private getServerDebugInfo(response: InstrumentedHttpDataResponse<RawRecommendedResponseV1>): string {
        if (!response || !response.headers) {
            return '';
        }

        const headers = response.headers;
        const backendServer = headers.get('X-CalculatedBETarget') || '';
        const frontendServer = headers.get('X-FEServer') || '';

        return `BE: ${backendServer}. FE: ${frontendServer}.`;
    }

    private async checkLocalFileAndProcess(item: RawRecommendedDocumentV1): Promise<MruDataItem> {
        return new Promise(resolve => {
            if (item.service_info?.service_id !== 'LocalFile') {
                resolve(this.convertToMruDataItem(item));
            } else if (this.capabilitiesService.capabilities.isPwaInUwp) {
                const filePath = item.web_url;
                try {
                    Windows.Storage.StorageFile.getFileFromPathAsync(filePath)
                        .then(storageFile => {
                            resolve(!!storageFile ? this.convertToMruDataItem(item, true) : null);
                        }, () => {
                            resolve(null);
                        });
                } catch (e) {
                    resolve(null);
                }
            } else {
                resolve(null);
            }
        });
    }
}
